// #genai Phase 2 "Add Credit Card Payment Method" route styles.
// Pixel-matched to Figma 269:21301 (empty) / 269:21393 (filled).
// Rendered at /billing-information/add-card behind the ENABLE_MULTI_PAYMENT_METHODS flag.
// Two numbered sections only (Payment Information / Billing Information) — the payment-type
// chooser was removed in the new design (the Bank vs Card choice now lives in the list dropdown).
import { makeStyles } from 'lib/components';

export default makeStyles((theme) => ({
  // #genai Mobile: the form fills the page's <main> column so the white card stretches full-height
  // and the Save/Cancel footer anchors to the bottom (matches the ACH mobile layout, Figma 269:21589).
  // Desktop is untouched (default block flow keeps the card's centered margin layout).
  formRoot: {
    '@media (max-width: 639px)': {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
  },

  // White card — bg white, gap 24, padding 24, radius 4, ev-elevation--z01 shadow.
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '24px',
    margin: '20px',
    borderRadius: '4px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.12), 0px 0px 2px 0px rgba(0,0,0,0.14)',
    // #genai Phase 2 inline error styling (Figma 269:21936 / 269:22011): errored MUI inputs (Card
    // type / Month / Year selects, name + address fields) get a #BC1616 border + #FFCDD1 fill.
    // Labels/helpers are already red via the field styles; hosted-field iframes use `hostedFieldError`.
    '& .myev-MuiOutlinedInput-root.myev-Mui-error': {
      backgroundColor: '#FFCDD1',
    },
    '& .myev-MuiOutlinedInput-root.myev-Mui-error .myev-MuiOutlinedInput-notchedOutline': {
      borderColor: '#BC1616',
    },
    // #genai Mobile (Figma 226:31575): full-bleed flat surface — no shadow, no margin, no radius.
    // Padding/gap are owned by the mobile grouper wrappers so Figma's hairline dividers sit flush.
    '@media (max-width: 639px)': {
      margin: '0px',
      padding: '0px',
      gap: '0px',
      borderRadius: '0px',
      boxShadow: 'none',
      // #genai Grow to fill <main> so the white surface runs full-height and the footer (margin-top
      // auto below) anchors to the bottom of the viewport.
      flexGrow: 1,
    },
  },

  // #genai Mobile sub-header (Figma 269:21589): back-arrow on the left + "Add Payment Method" title
  // centered in the full width (no X close icon in the new design). White bg, 16/24 padding, full
  // bleed. Hidden on desktop.
  mobileHeader: {
    display: 'none',
    '@media (max-width: 639px)': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      width: '100%',
      padding: '16px 24px',
      backgroundColor: '#FFFFFF',
      boxSizing: 'border-box',
    },
  },

  // #genai Mobile-header back button — absolutely positioned on the left so the title stays centered
  // in the full header width (Figma 269:21589).
  mobileHeaderIconBtn: {
    position: 'absolute',
    left: '24px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    padding: 0,
    border: 'none',
    background: 'none',
    color: '#231F20',
    cursor: 'pointer',
    '& svg': {
      width: '24px',
      height: '24px',
    },
    '&:hover': {
      opacity: 0.7,
    },
  },

  mobileHeaderTitle: {
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '24px',
    color: '#231F20',
  },

  // Inner sections column — gap 24, max width 800 per Figma 226:27681.
  // #genai Mobile: gap moves to the grouper wrappers so dividers can sit between groups.
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
    maxWidth: '800px',
    '@media (max-width: 639px)': {
      gap: '0px',
      maxWidth: 'none',
    },
  },

  // #genai Mobile-only grouper for Section 1 (Choose Payment Type) + Section 2 (Payment Info)
  // (Figma 226:31579). display: contents on desktop so the existing flex layout is untouched;
  // flex column with 16/24 padding + bottom hairline on mobile.
  mobilePaymentGroup: {
    display: 'contents',
    '@media (max-width: 639px)': {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      padding: '16px 24px',
      borderBottom: '1px solid #DCE0E9',
      boxSizing: 'border-box',
      width: '100%',
    },
  },

  // #genai Mobile-only grouper for Section 3 (Billing Address) + Make-default checkbox
  // (Figma 226:31587). Same pattern as mobilePaymentGroup; bottom hairline divides from the
  // Save/Cancel footer.
  mobileBillingGroup: {
    display: 'contents',
    '@media (max-width: 639px)': {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      padding: '16px 24px',
      borderBottom: '1px solid #DCE0E9',
      boxSizing: 'border-box',
      width: '100%',
    },
  },

  // "1) Choose Payment Type" / "2) Payment Information" / "3) Billing Information"
  sectionHeader: {
    fontFamily: 'Noto Sans',
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '27px',
    color: '#005A8D',
  },

  // "3) Billing Information" header + subtitle grouped tightly (gap 6) per Figma 226:27724.
  billingHeaderBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },

  // Subtitle under "3) Billing Information".
  billingSubtitle: {
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    color: '#4D5365',
  },

  // Indented sub-section under each numbered header (pl-24).
  subSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingLeft: '24px',
    // #genai Mobile (Figma 226:31575): fields run flush to the card edge (no indent). Inline
    // inline `maxWidth: 383px / 782px` on grid items inside the shared PaymentInformation /
    // AddressInformation components is overridden so every input takes the full row per Figma.
    '@media (max-width: 639px)': {
      paddingLeft: '0px',
      '& .myev-MuiGrid-item': {
        maxWidth: 'none !important',
      },
    },
  },

  // #genai Payment Information section bottom padding (Figma 269:21362 Save-consent block has
  // pb-24). Combined with the 24px sections gap this yields the 48px gap below the card-charge
  // disclaimer before "2. Billing Information". Mobile owns its spacing via the group wrappers.
  paymentSectionPad: {
    paddingBottom: '24px',
    '@media (max-width: 639px)': {
      paddingBottom: '0px',
    },
  },

  // Wrapper around the reused AddressInformation. Two scoped-only overrides (no change to the
  // shared component / Phase 1):
  //   1) hide its built-in Save/Cancel button row (this page renders its own footer)
  //   2) lay the address fields out as Figma's 2-column billing grid (226:27729) so the Primary
  //      Phone and Secondary phone rows sit side-by-side instead of stacked.
  addressWrap: {
    '& #submitOrderBtn, & [data-testid="cancelButton"]': {
      display: 'none',
    },
    // Root column → 2-column grid (gap-x 32 / gap-y 24 per Figma).
    '& > .myev-MuiGrid-root': {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      columnGap: '32px',
      rowGap: '24px',
      maxWidth: '776px',
    },
    // Every row spans both columns by default; grid rowGap replaces the per-field bottom margins.
    '& > .myev-MuiGrid-root > .myev-MuiGrid-root': {
      gridColumn: '1 / span 2',
      marginBottom: 0,
    },
    // Inner fields (inside the phone rows) drop their own bottom margin too.
    '& .myev-MuiGrid-container .myev-MuiGrid-item': {
      marginBottom: 0,
    },
    // #genai All billing field labels are Noto Sans SemiBold 16/24 in Figma. The shared
    // AddressInformation renders them at regular weight, so bump weight/line-height here (scoped to
    // this page only — Phase 1 is untouched).
    '& .myev-MuiFormLabel-root': {
      fontWeight: 600,
      lineHeight: '24px',
    },
    // #genai Company before Billing email (Figma) now comes from AddressInformation's own Phase 2
    // source order, so tab order matches the visual order — no CSS `order` swap here.
    // Primary Phone + Ext → left column; Secondary phone + Ext → right column (same row).
    '& > .myev-MuiGrid-root > .myev-MuiGrid-root:nth-child(4)': {
      gridColumn: '1',
    },
    '& > .myev-MuiGrid-root > .myev-MuiGrid-root:nth-child(5)': {
      gridColumn: '2',
    },
    // The internal button row is hidden above; collapse its empty grid cell.
    '& > .myev-MuiGrid-root > .myev-MuiGrid-root:nth-child(8)': {
      display: 'none',
    },
    // Mobile: single column, phones stack again.
    '@media (max-width: 639px)': {
      '& > .myev-MuiGrid-root': {
        gridTemplateColumns: '1fr',
      },
      '& > .myev-MuiGrid-root > .myev-MuiGrid-root:nth-child(4)': {
        gridColumn: '1',
      },
      '& > .myev-MuiGrid-root > .myev-MuiGrid-root:nth-child(5)': {
        gridColumn: '1',
      },
    },
  },

  // #genai First Name + Last Name row in Billing Information (names moved out of Payment
  // Information). Two equal 380px columns with a 16px gap (total 776px, matching the billing content
  // width); stacks full-width on mobile.
  nameRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    maxWidth: '776px',
    '& > *': {
      flex: 1,
    },
    '@media (max-width: 639px)': {
      flexDirection: 'column',
      gap: '24px',
      maxWidth: 'none',
    },
  },

  // #genai Field label for the name row — Noto Sans SemiBold 16/24 #4D5365, matching every other
  // billing field label in Figma.
  // #genai Phase 2 field-error UX: name labels stay charcoal on error (matches every other
  // billing field on this page); only the input box + helper text turn red.
  // #genai Flush label to the input left edge (UX feedback: lightly indented First/Last labels).
  nameLabel: {
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    color: theme.evPalette.charcoal[800],
    marginBottom: '8px',
    marginLeft: 0,
    paddingLeft: 0,
    width: 'fit-content',
    position: 'inherit',
    top: 'inherit',
    left: 'inherit',
    transform: 'inherit',
  },

  asterisk: {
    color: theme.evPalette.rose[900],
  },

  nameInputText: {
    width: '100%',
    '& > .Mui-error > fieldset': {
      borderColor: `${theme.evPalette.rose[900]} !important`,
    },
  },

  nameInputHelperText: {
    // #genai Align name errors with the input edge, matching Company and Billing email.
    marginLeft: '0 !important',
    color: `${theme.evPalette.rose[900]} !important`,
  },

  // "Make default payment method" checkbox row — at card-padding level (no extra indent) per Figma.
  // Negative margin pulls the 24px checkbox icon flush to the section-header left edge (MUI adds 9px padding).
  // #genai Shown on mobile too (below Billing Information) so users can toggle the default on responsive screens.
  defaultCheckbox: {
    '& .myev-MuiCheckbox-root': {
      marginLeft: '-9px',
    },
    '& .myev-MuiTypography-root': {
      fontFamily: 'Noto Sans',
      fontSize: '14px',
      fontWeight: 400,
      color: '#000000',
    },
  },

  // Footer button row — Add Payment Method + Cancel, 304-wide with space-between per Figma 226:27747.
  footer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '304px',
    // #genai Mobile (Figma 226:31608): stack full-width with the primary "Add Payment Method"
    // button on top + "Cancel" below. Gap 16 per Figma `gap-[16px]`. 16/24 padding so the buttons
    // share the section-wrapper rhythm.
    '@media (max-width: 639px)': {
      flexDirection: 'column',
      width: '100%',
      gap: '16px',
      padding: '16px 24px',
      boxSizing: 'border-box',
      // #genai Anchor the Save/Cancel group to the bottom of the full-height card so it sits just
      // above the global footer; collapses when the form content already fills the height.
      marginTop: 'auto',
    },
  },

  // "Add Payment Method" — disabled bg #E6EAF1 / text #B4BAC7; enabled bg #007CAD / white.
  addButton: {
    height: '44px',
    padding: '10px 16px',
    borderRadius: '4px',
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 500,
    letterSpacing: '0.32px',
    textTransform: 'none',
    backgroundColor: '#007CAD',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#005A8D',
    },
    '&.Mui-disabled': {
      backgroundColor: '#E6EAF1',
      color: '#B4BAC7',
    },
    '@media (max-width: 639px)': {
      width: '100%',
    },
  },

  // "Cancel" — white bg, 1px #007CAD border, text #007CAD.
  cancelButton: {
    height: '44px',
    padding: '10px 16px',
    borderRadius: '4px',
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 500,
    letterSpacing: '0.32px',
    textTransform: 'none',
    backgroundColor: '#FFFFFF',
    color: '#007CAD',
    border: '1px solid #007CAD',
    '&:hover': {
      backgroundColor: 'rgba(0,124,173,0.06)',
    },
    '@media (max-width: 639px)': {
      width: '100%',
    },
  },
}));
